import { useTranslation } from 'react-i18next';
import { useIndicadorsFiltrats } from '@/api/informes';
import { Icona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Buit } from '@/pages/operacio/comu';
import { csv, descarregaCsv } from '@/pages/planificacio/calendari';
import { Barres, SelectorPeriode } from './comu';
import { detectaPatrons, usePeriode, type Patro } from './suport';

const BADGE_PATRO: Record<Patro['clau'], { classe: string; clau: 'recurrent' | 'vigilar' }> = {
  MATEIX_SERVEI: { classe: 'badge--critic', clau: 'recurrent' },
  MATEIXA_ZONA: { classe: 'badge--incident', clau: 'recurrent' },
  CONCENTRACIO: { classe: 'badge--avis', clau: 'vigilar' },
};

export function W47Recurrents() {
  const { t } = useTranslation('informes');
  const f = useFormat();
  const { periode, setPeriode, desde, fins } = usePeriode('TRIMESTRE');
  const ind = useIndicadorsFiltrats({ desde, fins });
  const recurrents = [...(ind.data?.recurrents ?? [])].sort((a, b) => b.total - a.total);
  const patrons = detectaPatrons(recurrents);
  const perServei = new Map<string, number>();
  recurrents.forEach((r) => {
    const k = r.servei ?? '—';
    perServei.set(k, (perServei.get(k) ?? 0) + r.total);
  });
  const barres = [...perServei.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([servei, total], i) => ({
      clau: servei,
      etiqueta: servei,
      valor: total,
      alt: i % 2 === 1,
      titol: t('w47.barraTitol', { servei, count: total }),
    }));

  const exporta = () =>
    descarregaCsv('recurrents.csv', [
      csv([t('comu.servei'), t('comu.zona'), t('w40.kpi.activacions')]),
      ...recurrents.map((r) => csv([r.servei ?? '', r.zona ?? '', String(r.total)])),
    ]);

  return (
    <section className="pantalla" data-screen="W-47">
      <Toolbar titol={t('titols.recurrents')}>
        <span className="t-footnote dim">
          {t('comu.rang', { desde: f.data(desde), fins: f.data(fins) })}
        </span>
        <SelectorPeriode valor={periode} onCanvia={setPeriode} />
        <button
          type="button"
          className="btn btn--s"
          onClick={exporta}
          disabled={recurrents.length === 0}
        >
          <Icona nom="doc-export" />
          {t('comu.exporta')}
        </button>
      </Toolbar>
      <div className="content">
        {ind.isError ? (
          <ErrorCarrega onReintenta={() => void ind.refetch()} />
        ) : !ind.data ? (
          <Buit icona="arrow-clockwise">{t('comu.carregant')}</Buit>
        ) : recurrents.length === 0 ? (
          <div className="panel">
            <Buit icona="checkmark-circle">{t('w47.buit')}</Buit>
          </div>
        ) : (
          <div className="grid grid-sidebar">
            <div className="stack">
              <div className="panel">
                <div className="panel__head">
                  <h3>{t('w47.taula')}</h3>
                </div>
                <table className="taula" data-testid="recurrents">
                  <thead>
                    <tr>
                      <th>{t('comu.servei')}</th>
                      <th>{t('comu.zona')}</th>
                      <th className="num">{t('w40.kpi.activacions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recurrents.map((r) => (
                      <tr key={`${r.serveiId}-${r.zonaId}`}>
                        <td>{r.servei ?? '—'}</td>
                        <td className={r.zona ? undefined : 'dim'}>
                          {r.zona ?? t('comu.senseZona')}
                        </td>
                        <td className="num">{f.numero(r.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="panel">
                <div className="panel__head">
                  <h3>{t('w47.grafic')}</h3>
                </div>
                <div className="panel__body">
                  <Barres dades={barres} etiqueta={t('w47.grafic')} />
                </div>
              </div>
            </div>
            <div className="panel">
              <div className="panel__head">
                <h3>{t('w47.patrons')}</h3>
              </div>
              <div className="panel__body stack" style={{ gap: 12 }} data-testid="patrons">
                {patrons.length === 0 ? (
                  <Buit icona="checkmark-circle">{t('w47.sensePatrons')}</Buit>
                ) : (
                  patrons.map((p, i) => (
                    <div key={`${p.clau}-${i}`} className="patro">
                      <div className="hstack gap-8">
                        <span className={`badge ${BADGE_PATRO[p.clau].classe}`}>
                          {t(`w47.etiquetes.${BADGE_PATRO[p.clau].clau}`)}
                        </span>
                        <span className="t-caption-2 dim">{t(`periodes.${periode}`)}</span>
                      </div>
                      <div className="patro__titol">{t(`w47.patro.${p.clau}.titol`, p.params)}</div>
                      <div className="patro__text">
                        {t('w47.recomanacio', {
                          text: t(`w47.patro.${p.clau}.recomanacio`, p.params),
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
